import I18n from 'I18n';
import { List, fromJS } from 'immutable';
import { memo, useEffect } from 'react';
import { CURRENCY, DURATION, NUMBER, PERCENT } from 'reporting-data/constants/property-types';
import { DISTINCT_APPROX } from 'reporting-data/constants/metricTypes';
import { PERCENTAGE } from 'reporting-data/constants/numberDisplayHints';
import { getProperty, getPropertyLabel, getReference, getReferenceLabel, getNumberDisplayHint } from 'reporting-data/v2/dataset/datasetRecords';
import { DEFAULT_DEBUG } from '../../constants/debug';
import { TABLE } from '../../constants/visualizationTypes';
import { getPropertyFormatter, formatDecimalColumnToPercent, isFormattableProperty
//@ts-expect-error Untyped import
} from '../../formatters/propertyFormatter';
//@ts-expect-error Untyped import
import { getCustomRenderer } from './components/customCellRenderers';
import TableRenderer from './components/TableRenderer';
import { Alignments, Footer, TableVisualization } from './tableRecords';
//@ts-expect-error Untyped import
import { pivotDatasetKey } from '../pivot/pivotVisualization';
import { getConditionalFormatter } from '../../utils/conditionalFormatterUtils';
import { useConditionalPalettes } from '../../hooks/useConditionalPalettes';
import { jsx as _jsx } from "react/jsx-runtime";
const RIGHT_ALIGN_TYPES = [PERCENT, CURRENCY, NUMBER, DURATION];
const getDefaultColumnAlignment = column => {
  const {
    dataset,
    field,
    index,
    showAsBarChart,
    renderCell
  } = column;
  const {
    properties
  } = dataset;
  const property = properties.get(field);
  if (index === 0 || showAsBarChart || renderCell || !property) {
    return Alignments.left;
  }
  const type = property.get('type');
  const isRightAlignedProperty = RIGHT_ALIGN_TYPES.includes(type);
  const aggregationType = property.getIn(['propertyMeta', 'aggregationType']);
  return isRightAlignedProperty || DISTINCT_APPROX === aggregationType ? Alignments.right : Alignments.left;
};
export const type = TABLE;
export const record = TableVisualization;
const mapVisualization = (visualization, conditionalPalettes) => {
  const {
    data,
    columns: providedColumns = List(),
    footer: footerDatasetKey = '',
    pages
  } = visualization;
  const columns = providedColumns.toList();
  if (data.delete(footerDatasetKey).map(dataset => (dataset.get('data') || List()).count()).toSet().count() > 1) {
    throw new Error('Table requires all datasets to be of the same size');
  }
  const hasPivotData = data && data.has(pivotDatasetKey);
  const footerDataset = data.get(footerDatasetKey);
  const footerRow = footerDataset && footerDataset.getIn(['data', '0']);
  const updatedColumns = columns.map((col, index) => {
    var _tableStyle$format;
    const {
      field,
      customRendererKey
    } = col;
    const datasetIdentifier = col.get('dataset');
    const updatedDataset = datasetIdentifier ? data.get(datasetIdentifier).set('key', datasetIdentifier) : data.first();
    const columnWithUpdatedDataset = col.set('index', index).set('dataset', updatedDataset);
    let columnWithAlignAndLabel = columnWithUpdatedDataset.update('align', align => align || getDefaultColumnAlignment(columnWithUpdatedDataset)).update('label', label => label || getPropertyLabel(updatedDataset, field));
    const tableStyle = (columnWithAlignAndLabel.get('style') || fromJS({})).toJS();
    columnWithAlignAndLabel = tableStyle !== null && tableStyle !== void 0 && (_tableStyle$format = tableStyle.format) !== null && _tableStyle$format !== void 0 && (_tableStyle$format = _tableStyle$format.conditionalFormatting) !== null && _tableStyle$format !== void 0 && _tableStyle$format.useConditionalFormatting && conditionalPalettes ? columnWithAlignAndLabel.set('conditionalFormatter', getConditionalFormatter(updatedDataset, field, tableStyle, conditionalPalettes)) : columnWithAlignAndLabel;
    let footer;
    if (footerDatasetKey) {
      const maybeFormattableProperty = getProperty(footerDataset, field);
      const property = columnWithAlignAndLabel.get('formattingObject') && isFormattableProperty(maybeFormattableProperty) ? maybeFormattableProperty.set('formattingType', columnWithAlignAndLabel.getIn(['formattingObject', 'type'])).set('formattingObject', columnWithAlignAndLabel.get('formattingObject')) : maybeFormattableProperty;
      const formatPropertyValue = property && getPropertyFormatter(property) || (val => val);
      const footerValue = footerRow && footerRow.get(field);
      const footerLabel = getReference(footerDataset, field, footerValue) ? getReferenceLabel(footerDataset, field, footerValue) : formatPropertyValue(footerValue);
      let label;
      if (index === 0) {
        label = hasPivotData && pages && pages > 1 ? I18n.text('report.details.header.PAGE_TOTAL') : I18n.text('report.details.header.REPORT_TOTAL');
      } else {
        label = footerValue || footerValue === 0 ? footerLabel : undefined;
      }
      footer = Footer({
        dataset: footerDatasetKey,
        field,
        value: footerValue,
        label
      }).set('dataset', footerDataset);
    }
    const columnWithFooter = footer ? columnWithAlignAndLabel.set('footer', footer) : columnWithAlignAndLabel;
    let formattedData;
    const numberDisplayHint = getNumberDisplayHint(updatedDataset, field);
    if (numberDisplayHint === PERCENTAGE && !columnWithFooter.get('formattingObject')) {
      formattedData = formatDecimalColumnToPercent(updatedDataset.get('data'), field);
      return columnWithFooter.set('useRawDataLabel', true).setIn(['dataset', 'data'], formattedData);
    }
    const columnWithFormattedData = formattedData ? columnWithFooter.set('useRawDataLabel', true).setIn(['dataset', 'data'], formattedData) : columnWithFooter;
    return customRendererKey ? columnWithFormattedData.set('renderCell', getCustomRenderer(customRendererKey)) : columnWithFormattedData;
  });
  return visualization.set('columns', updatedColumns);
};
export const TableViz = ({
  visualization,
  debug = DEFAULT_DEBUG,
  onReportVisualize
}) => {
  const conditionalPalettes = useConditionalPalettes();
  const mappedVisualization = mapVisualization(visualization, conditionalPalettes);
  useEffect(() => {
    debug.set('TableVisualization', mappedVisualization);
  }, [mappedVisualization, debug]);
  const table = /*#__PURE__*/_jsx(TableRenderer, {
    visualization: mappedVisualization,
    debug: debug,
    onReportVisualize: onReportVisualize
  });
  return table;
};
export const Render = /*#__PURE__*/memo(TableViz);
export const __TESTABLE__ = {
  mapVisualization
};